import _ from 'lodash';
import { computed, watch, onBeforeUnmount } from 'vue-demi';
import { Auth } from '@or-sdk/auth';
import { createAuthStore } from '@onereach/auth-ui-module';
import { storeToRefs } from 'pinia';

import { useOneReachConfig, useGlobalVars } from '@/composables';
import { useAgentUIWorker } from '@/apps/agent-ui-worker';

interface GlobalAuth {
  $ORAuthAPI: Auth;
  $ORAuthStore: ReturnType<ReturnType<typeof createAuthStore>>;
}

/**
 * We can't create auth API or store instances before receiving config.
 * We cache results so we won't have to re-create them multiple times.
 */
export const useOneReachAuth = (overrides: any = {}) => {
  const $global = useGlobalVars().getVueGlobals() as GlobalAuth;

  const getAPIInstance = () => {
    if (!_.isEmpty($global.$ORAuthAPI)) {
      return $global.$ORAuthAPI;
    }

    const ORConfig = useOneReachConfig().getORConfig();
    $global.$ORAuthAPI = new Auth({
      cookieDomain: overrides.cookieDomain ?? ORConfig.COOKIE_DOMAIN,
      cookiePrefix: ORConfig.COOKIE_NAME_PREFIX_NEW,
      authUiUrl: `${ORConfig.SDK_API_URL}/auth/token`,
      allowGuestLogin: Boolean(ORConfig.GUEST_LOGIN),
      userCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_name`,
      userExpireCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_user_expire`,
      multiUserCookieName: `${ORConfig.COOKIE_NAME_PREFIX}_multi`,
      expireInLong: Number(`${ORConfig.COOKIE_EXPIRATION_LONG}`),
      expireInShort: Number(`${ORConfig.COOKIE_EXPIRATION_SHORT}`),
      sdkUrl: `${ORConfig.SDK_API_URL}`,
      allowIframe: Boolean(ORConfig.ALLOW_IFRAME),
    });

    return $global.$ORAuthAPI;
  };

  const getStore = () => {
    if (!_.isEmpty($global.$ORAuthStore)) {
      return $global.$ORAuthStore;
    }

    /* TODO: "@onereach/auth-ui-module" has hardcoded "@or-sdk/auth" version */
    $global.$ORAuthStore = createAuthStore(getAPIInstance() as any)();
    return $global.$ORAuthStore;
  };

  const getRefs = () => storeToRefs(getStore());
  const authStore = getStore();
  const {
    authorized,
    cookiesExpired,
    token,
    reactiveToken,
  } = getRefs();

  const isAuthReady = computed(() => {
    return Boolean(
      authorized.value
      && !cookiesExpired.value
      && token.value
    );
  });

  const onUpdateCookieExpirationEvent = () => {
    authStore.updateCookieExpiration();
  };

  const { expireWSToken, updateWSToken } = useAgentUIWorker();

  const TOKEN_SYNC_INTERVAL_MS = 1000;

  const syncTokenFromAuth = () => {
    if (cookiesExpired.value || !authorized.value) {
      return;
    }

    const user = (getAPIInstance() as any)._getUser;
    if (!user?.token) {
      return;
    }

    authStore.updateReactiveToken({ token: user.token, expire: user.expire });
  };

  const tokenSyncInterval = window.setInterval(syncTokenFromAuth, TOKEN_SYNC_INTERVAL_MS);
  onBeforeUnmount(() => {
    window.clearInterval(tokenSyncInterval);
  });
  watch(reactiveToken, (newToken, oldToken) => {
    if (!newToken || newToken === oldToken || !oldToken) {
      return;
    }
    updateWSToken(newToken);
  });

  watch(cookiesExpired, (expired, wasExpired) => {
    if (expired) {
      expireWSToken();
    } else if (wasExpired) {
      authStore.updateCookieExpiration();
    }
  });

  return {
    getAPIInstance,
    getStore,
    getRefs,
    isAuthReady,
    onUpdateCookieExpirationEvent,
  };
};
